'use client'

import { useEffect, useRef } from 'react'

/**
 * Subtle animated nebula field for the dark v2 hero background. Pure
 * progressive enhancement: a WebGL2 (falling back to WebGL1) fragment
 * shader draws layered, slowly drifting noise behind the hero copy. Any
 * missing capability, opt-out signal, or runtime error leaves the hero
 * rendering exactly as it does without this component (a plain canvas
 * with opacity 0, which has no layout or visual footprint).
 *
 * The canvas is only ever revealed after a frame has actually drawn
 * successfully (checked via gl.isContextLost() and gl.getError(), never by
 * backing-store dimensions alone). `data-nebula-state` on the canvas element
 * mirrors this for tests: `idle` (mounted, no confirmed frame yet), `drawing`
 * (revealed, at least one clean frame drawn) or `hidden` (no context, a lost
 * context, a failed draw, or a runtime error; permanently disabled for this
 * mount). A `webglcontextlost` listener hides immediately (no fade-out) if
 * loss happens after reveal.
 *
 * Fallback matrix (all silent, no fallback UI):
 *  - no WebGL2/WebGL1 context, or the obtained context is already lost: hidden
 *  - prefers-reduced-motion: one static frame; revealed only if it drew cleanly
 *  - navigator.connection.saveData or navigator.deviceMemory <= 2: hidden
 *  - a thrown error during setup or during any later frame: caught, hidden
 *  - a non-throwing GL error on any frame (checked via gl.getError()): hidden
 *  - context lost after a successful reveal: hidden immediately, loop stopped
 *  - tab hidden or hero scrolled offscreen: animation loop pauses (resumes on return)
 *
 * Lifecycle contract:
 *  - cleanup frees this mount's program and buffer and never calls
 *    `WEBGL_lose_context.loseContext()`. A canvas has exactly one WebGL
 *    context for its lifetime, so forcing it lost on cleanup killed the
 *    context that the surviving mount of a Strict Mode double-invoke then
 *    tried to draw with.
 *  - every effect run starts by clearing the inline opacity and transition
 *    a previous run may have written, so a rerun on the same canvas is not
 *    stuck hidden or stuck without its fade.
 *  - cleanup never throws past the fallback: teardown is fully guarded, so a
 *    failure while freeing resources cannot escape into React or out of the
 *    animation loop's own catch.
 *
 * Colours are sampled from the choice-graphics dark tokens in
 * src/app/choice-graphics.css (.cw-dark block): --cw-ink-soft, a lighter
 * ink-soft/paper mix standing in for --surface-tertiary (v3's version is a
 * derived color-mix(), not a fixed hex, so this shader approximates it),
 * --cw-lime (the v3 teal accent; the token name is a stable internal
 * identifier, not a colour claim), and the --info cyan used
 * as a restrained cool glint. Kept intentionally faint (peak alpha ~0.22)
 * so the Anton display title stays fully readable over it.
 */

interface NavigatorWithHints extends Navigator {
  deviceMemory?: number
  connection?: { saveData?: boolean }
}

const VERTEX_SRC = `
attribute vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`

// Fullscreen-triangle vertex shader pairs with this fragment shader. Written
// as plain ESSL 1.00 (no #version pragma) so it compiles unchanged under
// both a WebGL1 and a WebGL2 context.
const FRAGMENT_SRC = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec2 uResolution;
uniform float uTime;
uniform float uSeed;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

// Classic simplex noise (permutation-polynomial formulation), 3D so time
// can ride the third axis without a seam.
float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

float fbm(vec3 p) {
  float sum = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 5; i++) {
    sum += amp * snoise(p);
    p *= 2.02;
    amp *= 0.5;
  }
  return sum;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution.xy;
  vec2 aspectUv = (uv - 0.5) * vec2(uResolution.x / uResolution.y, 1.0);

  float t = uTime;

  // Domain warp: a slow field bends the coordinates fed to the main field,
  // which is what reads as smoky drift rather than plain rolling noise.
  vec3 warpP = vec3(aspectUv * 1.1, uSeed);
  vec2 warp = vec2(
    fbm(warpP + vec3(0.0, 0.0, t * 0.6)),
    fbm(warpP + vec3(5.2, 1.3, t * 0.6))
  );

  vec3 p = vec3(aspectUv * 1.6 + warp * 0.35, uSeed + t * 0.35);
  float n = fbm(p);
  n = n * 0.5 + 0.5;

  vec3 base = vec3(0.153, 0.259, 0.349); /* --cw-ink-soft */
  vec3 warm = vec3(0.248, 0.340, 0.415); /* --surface-tertiary approximation, see header note */
  vec3 accentTeal = vec3(0.106, 0.600, 0.545); /* --cw-lime (v3 teal accent) */
  vec3 infoCyan = vec3(0.490, 0.812, 1.000); /* --info cyan */

  vec3 cloud = mix(base, warm, smoothstep(0.35, 0.75, n));

  float accentGlint = smoothstep(0.72, 0.95, fbm(p * 1.4 + vec3(2.0, -3.0, t * 0.25)));
  float infoGlint = smoothstep(0.74, 0.96, fbm(p * 1.7 + vec3(-4.0, 2.5, t * 0.2)));
  cloud = mix(cloud, accentTeal, accentGlint * 0.35);
  cloud = mix(cloud, infoCyan, infoGlint * 0.28);

  // Fade the field out toward the section edges instead of ending on a
  // hard-edged rectangle.
  float vignette = smoothstep(0.95, 0.25, length(aspectUv));

  float alpha = n * 0.22 * vignette;

  // Dither in a hash of the screen position, small enough to kill banding
  // in the low-alpha gradient without reading as visible noise.
  float dither = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
  alpha += (dither - 0.5) * 0.01;
  alpha = clamp(alpha, 0.0, 1.0);

  gl_FragColor = vec4(cloud * alpha, alpha);
}
`

/**
 * Best-effort release. Every delete is independent so one failing object
 * cannot skip the rest, and a failure here never masks the original error.
 */
function releaseAll(
  gl: WebGLRenderingContext | WebGL2RenderingContext,
  objects: Array<{ kind: 'shader' | 'program' | 'buffer'; ref: WebGLShader | WebGLProgram | WebGLBuffer | null }>
): void {
  for (const { kind, ref } of objects) {
    if (!ref) continue
    try {
      if (kind === 'shader') gl.deleteShader(ref as WebGLShader)
      else if (kind === 'program') gl.deleteProgram(ref as WebGLProgram)
      else gl.deleteBuffer(ref as WebGLBuffer)
    } catch {
      // A dead context reclaims its own objects; keep releasing the others.
    }
  }
}

function compileShader(
  gl: WebGLRenderingContext | WebGL2RenderingContext,
  type: number,
  source: string
): WebGLShader {
  const shader = gl.createShader(type)
  if (!shader) throw new Error('hero-nebula: createShader failed')
  // shaderSource, compileShader and getShaderParameter can all throw on a
  // context that dies mid-setup, each after the shader was allocated.
  let ok = false
  try {
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader)
      throw new Error(`hero-nebula: shader compile failed: ${info ?? 'unknown error'}`)
    }
    ok = true
    return shader
  } finally {
    if (!ok) releaseAll(gl, [{ kind: 'shader', ref: shader }])
  }
}

/**
 * Links the shader pair into a program. Every allocation between here and
 * the successful return is released on any failure path: attachShader,
 * linkProgram, getProgramParameter and detachShader can each throw after
 * the program and both shaders exist.
 */
function createProgram(gl: WebGLRenderingContext | WebGL2RenderingContext): WebGLProgram {
  let vertexShader: WebGLShader | null = null
  let fragmentShader: WebGLShader | null = null
  let program: WebGLProgram | null = null
  let ok = false
  try {
    vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SRC)
    fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SRC)
    program = gl.createProgram()
    if (!program) throw new Error('hero-nebula: createProgram failed')
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program)
      throw new Error(`hero-nebula: program link failed: ${info ?? 'unknown error'}`)
    }
    // Shaders are only needed for the link step; detaching is best effort so
    // a detach failure cannot lose the linked program.
    try {
      gl.detachShader(program, vertexShader)
      gl.detachShader(program, fragmentShader)
    } catch {
      // Keep the program; the shaders are still deleted below.
    }
    ok = true
    return program
  } finally {
    // Shaders are always released: on success they are no longer needed, on
    // failure they must not leak. The program is released only on failure.
    releaseAll(gl, [
      { kind: 'shader', ref: vertexShader },
      { kind: 'shader', ref: fragmentShader },
      ...(ok ? [] : [{ kind: 'program' as const, ref: program }]),
    ])
  }
}

export function HeroNebula() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let cancelled = false
    let rafId = 0
    let observer: IntersectionObserver | null = null
    let isOnscreen = true
    let isTabVisible = document.visibilityState !== 'hidden'
    let onVisibilityChange: (() => void) | null = null
    let onContextLost: ((e: Event) => void) | null = null
    let revealed = false
    // Tracked from the moment each object is allocated, not once setup
    // finishes, so a throw part-way through setup still frees what exists.
    let glCtx: WebGLRenderingContext | WebGL2RenderingContext | null = null
    let glProgram: WebGLProgram | null = null
    let glBuffer: WebGLBuffer | null = null

    // Frees this mount's GL objects. Never calls
    // WEBGL_lose_context.loseContext(); see the header comment.
    function releaseGl() {
      const gl = glCtx
      const program = glProgram
      const buffer = glBuffer
      glProgram = null
      glBuffer = null
      glCtx = null
      if (!gl) return
      let alive = false
      try {
        alive = !gl.isContextLost()
      } catch {
        // Treat an unreadable context as dead: it reclaims its own objects.
      }
      if (!alive) return
      releaseAll(gl, [
        { kind: 'program', ref: program },
        { kind: 'buffer', ref: buffer },
      ])
    }

    // Cancels the loop, removes listeners, frees GL objects. Every step is
    // guarded: teardown runs from React cleanup and from inside the loop's
    // own catch, and must not throw past either.
    function teardown() {
      cancelled = true
      try {
        if (rafId) cancelAnimationFrame(rafId)
      } catch {
        // ignore
      }
      try {
        observer?.disconnect()
      } catch {
        // ignore
      }
      try {
        if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange)
        if (onContextLost) canvas!.removeEventListener('webglcontextlost', onContextLost)
      } catch {
        // ignore
      }
      releaseGl()
    }

    // Disables the effect for the rest of this mount: no context, a lost
    // context, a failed draw, or a runtime exception all land here. Hides
    // without a fade even if a reveal transition was in flight. The hide
    // happens before teardown so a teardown failure cannot leave it visible.
    function markHidden() {
      try {
        canvas!.style.transition = 'none'
        canvas!.style.opacity = '0'
        canvas!.dataset.nebulaState = 'hidden'
      } catch {
        // ignore
      }
      teardown()
    }

    // A previous run on this same canvas (Strict Mode's double invoke, or any
    // later rerun) may have left inline opacity and transition behind. Clear
    // both so this run starts from the stylesheet's hidden default and can
    // still fade in on reveal.
    canvas.style.transition = ''
    canvas.style.opacity = '0'
    canvas.dataset.nebulaState = 'idle'

    try {
      // Opt-out signals, checked before any WebGL work.
      const nav = navigator as NavigatorWithHints
      if (nav.connection?.saveData) {
        markHidden()
        return
      }
      if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) {
        markHidden()
        return
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      const contextAttributes: WebGLContextAttributes = {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: true,
        preserveDrawingBuffer: false,
        powerPreference: 'low-power'
      }

      const gl: WebGLRenderingContext | WebGL2RenderingContext | null =
        (canvas.getContext('webgl2', contextAttributes) as WebGL2RenderingContext | null) ??
        (canvas.getContext('webgl', contextAttributes) as WebGLRenderingContext | null) ??
        (canvas.getContext('experimental-webgl', contextAttributes) as WebGLRenderingContext | null)

      if (!gl || gl.isContextLost()) {
        markHidden()
        return
      }
      glCtx = gl

      onContextLost = (e: Event) => {
        e.preventDefault()
        markHidden()
      }
      canvas.addEventListener('webglcontextlost', onContextLost)

      function frameIsHealthy(): boolean {
        return !gl!.isContextLost() && gl!.getError() === gl!.NO_ERROR
      }

      const program = createProgram(gl)
      glProgram = program
      gl.useProgram(program)

      const aPosition = gl.getAttribLocation(program, 'aPosition')
      const uResolution = gl.getUniformLocation(program, 'uResolution')
      const uTime = gl.getUniformLocation(program, 'uTime')
      const uSeed = gl.getUniformLocation(program, 'uSeed')

      // Fullscreen triangle: (-1,-1), (3,-1), (-1,3). Covers the whole
      // clip-space viewport with one triangle and no seam down the middle.
      const buffer = gl.createBuffer()
      if (!buffer) {
        markHidden()
        return
      }
      glBuffer = buffer
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
      gl.enableVertexAttribArray(aPosition)
      gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0)

      gl.clearColor(0, 0, 0, 0)

      // Seeded per mount, generated client-side only so SSR output never
      // varies and there is no hydration mismatch to reconcile.
      const seed = Math.random() * 1000

      let lastW = 0
      let lastH = 0

      function resize() {
        const rect = canvas!.getBoundingClientRect()
        const cssW = Math.max(1, rect.width)
        const cssH = Math.max(1, rect.height)
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
        // Render below the display resolution; the browser's own upscale
        // blur when the canvas is stretched back up via CSS is part of the
        // soft, low-detail look this needs.
        const renderScale = 0.55
        const w = Math.max(1, Math.round(cssW * dpr * renderScale))
        const h = Math.max(1, Math.round(cssH * dpr * renderScale))
        if (w === lastW && h === lastH) return
        lastW = w
        lastH = h
        canvas!.width = w
        canvas!.height = h
        gl!.viewport(0, 0, w, h)
      }

      function drawFrame(time: number) {
        resize()
        gl!.uniform2f(uResolution, lastW, lastH)
        gl!.uniform1f(uTime, time)
        gl!.uniform1f(uSeed, seed)
        gl!.clear(gl!.COLOR_BUFFER_BIT)
        gl!.drawArrays(gl!.TRIANGLES, 0, 3)
      }

      if (prefersReducedMotion) {
        try {
          drawFrame(0)
        } catch {
          markHidden()
          return
        }
        if (!frameIsHealthy()) {
          markHidden()
          return
        }
        // Under reduced motion the reveal itself must not animate. The
        // canvas class carries a 1s opacity transition and there is no
        // global prefers-reduced-motion rule in the app stylesheets to
        // suppress it, so this path opts out explicitly.
        canvas.style.transition = 'none'
        canvas.style.opacity = '1'
        canvas.dataset.nebulaState = 'drawing'
        // No animation loop and nothing to observe: teardown is a no-op
        // beyond removing the contextlost listener, kept for symmetry with
        // the animated path.
        return () => teardown()
      }

      // Throttle to roughly 28fps; the drift is slow enough that a full
      // 60fps loop would be pure waste.
      const frameInterval = 1000 / 28
      let lastFrameAt = 0
      let startedAt = 0

      function loop(now: number) {
        if (cancelled) return
        rafId = requestAnimationFrame(loop)
        // The whole body runs from a rAF callback, long after the effect's
        // own try/catch returned. An exception here (from drawFrame, from
        // the health check, or from the DOM writes inside markHidden) would
        // otherwise escape to the global error handler and leave a revealed
        // canvas frozen at opacity 1 with a dead loop. Contain everything.
        try {
          if (!isOnscreen || !isTabVisible) return
          if (now - lastFrameAt < frameInterval) return
          lastFrameAt = now
          if (!startedAt) startedAt = now
          const elapsed = (now - startedAt) / 1000
          // Scaled way down: a full drift cycle takes minutes, not seconds.
          drawFrame(elapsed * 0.05)
          if (!frameIsHealthy()) {
            markHidden()
            return
          }
          if (!revealed) {
            revealed = true
            canvas!.style.opacity = '1'
            canvas!.dataset.nebulaState = 'drawing'
          }
        } catch {
          markHidden()
        }
      }

      rafId = requestAnimationFrame(loop)

      observer = new IntersectionObserver(
        (entries) => {
          isOnscreen = entries[0]?.isIntersecting ?? true
        },
        { threshold: 0 }
      )
      observer.observe(canvas)

      onVisibilityChange = () => {
        isTabVisible = document.visibilityState !== 'hidden'
      }
      document.addEventListener('visibilitychange', onVisibilityChange)

      return () => teardown()
    } catch {
      // Any setup failure (shader compile, context creation, and so on):
      // leave the canvas hidden. The hero renders exactly as it does
      // without this component.
      markHidden()
      return
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden='true'
      data-nebula-state='idle'
      className='pointer-events-none absolute inset-0 h-full w-full opacity-0 transition-opacity duration-1000 ease-out'
    />
  )
}
